import React, { useEffect, useMemo, useState } from "react";

/**
 * Sprint 60.142 — Manual Optimizer Onboarding Tour V2.
 *
 * 7-step walkthrough for the refreshed Manual Optimizer surface.
 * Positions the page around its two headline differentiators
 * (Fingerprint Variance + Cluster Rotation) and the new Conflict
 * Detection Banner. Auto-fires once for every account/browser combo
 * and can be replayed anytime via the header "? Tour" button.
 *
 * Storage key bumped to _v2 so pre-Sprint-142 users who dismissed the
 * old Raw Optimizer tour see this refreshed version exactly once.
 *
 * Anchors target `data-tour-id` first with `data-testid` fallback so a
 * page-level DOM refactor doesn't silently break the tour. If a step's
 * anchor is missing the coach card renders center-screen (no highlight
 * ring) so the copy is still readable.
 */

const STORAGE_KEY = "wp_raw_tour_seen_v2";

const STEPS = [
  {
    id: "welcome",
    anchor: null, // centered — introductory step
    title: "Welcome to the Manual Optimizer",
    body:
      "Your rules. Your math. Full manual control PLUS two edge layers no other manual optimizer ships: Unique Seed (per-user projection jitter) and Peer Swap (post-solve rotation of projection-equivalent players). Every enhancement is a checkbox — turn them off for pure math. Let's take a 90-second tour.",
    side: "bottom",
  },
  {
    id: "pool",
    anchor: "raw-pool-tab-strip",
    fallbackAnchor: "raw-pool-search",
    title: "1 · The Player Pool",
    body:
      "This is your DK slate — every eligible player with our projection, ownership read, salary, and value. Lock a player to force them into every lineup, or Exclude to shove them into the Removed tab. Click any column header to sort — rookies without a projection yet are ranked by their average points per game so the order stays meaningful.",
    side: "bottom",
  },
  {
    id: "stackmix",
    anchor: "raw-stack-pct-none",
    fallbackAnchor: "raw-boost-mode-toggle",
    title: "2 · Stack Topology",
    body:
      "Set what % of your lineups run Unstacked, QB+1 (Single Stack), or QB+2 (Double Stack). A common split: 0 / 30 / 70. The three fields must sum to 100 — if they don't, the Conflict Banner up top will flash a red warning with an auto-fix button.",
    side: "bottom",
  },
  {
    id: "diversity",
    anchor: "raw-diversity-toggles",
    fallbackAnchor: "raw-fingerprint-variance-toggle",
    title: "3 · Portfolio Diversity — the WarPaint edge",
    body:
      "Two toggles you won't find in any manual optimizer. Unique Seed applies a per-user projection jitter from your account so no two subscribers ever build identical lineups. Peer Swap runs AFTER the solver, swapping statistically-equivalent peers (same position, ±5% projection, ±$400 salary) to widen exposure without giving up EV. Both are opt-in. Turn either off for deterministic (backtest-repeatable) runs.",
    side: "top",
  },
  {
    id: "grouprules",
    anchor: "raw-config-presets",
    fallbackAnchor: "raw-rule-presets",
    title: "4 · Group Rules & Presets",
    body:
      'Build correlation rules like "IF Mahomes → ≥2 of {Kelce, Rice, MVS}" or use one of the 9 one-click presets (Cap Chalk, Bring-Back, Force 3-Man Game Stack, etc.). Presets are toggles — click once to activate (green ✓), click again to remove. Team-scoped rules resolve dynamically at build time so late scratches never break them.',
    side: "top",
  },
  {
    id: "conflicts",
    anchor: null,
    title: "5 · Conflict Detection Banner",
    body:
      "The banner watches your entire config in real time. If your stack % doesn't sum to 100, if a Multi-QB target is too aggressive, if you've stacked a Bring-Back preset on top of a Bring-Back checkbox, or if you've asked for 20% Naked QB while forcing a 3-man game stack — it flags every conflict with a one-click Fix button. Never solves a bad build silently.",
    side: "bottom",
  },
  {
    id: "generate",
    anchor: "raw-diversity-chip",
    fallbackAnchor: "raw-generate-btn",
    title: "6 · Generate — with per-user variance",
    body:
      "The Diversity Chip next to Generate shows Unique Seed & Peer Swap status at a glance. Hit Generate and the ILP builds your batch inside 5-15 seconds for a 20-lineup Milly build. Then use 🎲 Randomize to shuffle stat-equivalent peers with a fresh seed, or ⚠ Auto-Swap Scratched to push a replacement into every affected lineup if a player is ruled out before lock.",
    side: "bottom",
  },
  {
    id: "export",
    anchor: "raw-lineups-expand-all",
    fallbackAnchor: "raw-randomize-lineups",
    title: "7 · Export CSV → DraftKings",
    body:
      "Click Download DK CSV to save a DK-native lineup file. Head to DraftKings → Contests → your entered contest → Edit Entries → Upload CSV and pick the file WarPaint just saved. DK ingests it in seconds. Bonus: Save Scenario snapshots the entire batch under a name, and Compare Vs shows exposure diffs versus any saved scenario — great for A/B testing two builds head-to-head. Ready to build?",
    side: "top",
  },
];

export default function RawOnboardingTour({ forceOpen = false, onDone }) {
  const [visible, setVisible] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  // Boot on mount — only run if never dismissed OR forceOpen
  useEffect(() => {
    if (forceOpen) {
      setVisible(true);
      setIdx(0);
      return;
    }
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        // Delay so first-mount API probes settle
        const t = setTimeout(() => setVisible(true), 900);
        return () => clearTimeout(t);
      }
    } catch { /* storage disabled */ }
  }, [forceOpen]);

  const step = STEPS[idx] || null;

  // Recompute anchor rect on each step + on window resize
  useEffect(() => {
    if (!visible || !step) return;
    // Non-anchored steps (welcome, conflict banner explainer) render centered
    if (!step.anchor) {
      setRect(null);
      return;
    }
    const findAnchor = () => {
      const primary = document.querySelector(`[data-tour-id="${step.anchor}"]`);
      if (primary) return primary;
      const testidPrimary = document.querySelector(`[data-testid="${step.anchor}"]`);
      if (testidPrimary) return testidPrimary;
      if (step.fallbackAnchor) {
        const fb = document.querySelector(`[data-testid="${step.fallbackAnchor}"]`);
        if (fb) return fb;
      }
      return null;
    };

    const measure = () => {
      const el = findAnchor();
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({
        top: r.top + window.scrollY,
        left: r.left + window.scrollX,
        width: r.width,
        height: r.height,
      });
      // Auto-scroll into view
      el.scrollIntoView({ block: "center", behavior: "smooth" });
    };

    measure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [visible, step, idx]);

  const finish = (viaSkip = false) => {
    try { localStorage.setItem(STORAGE_KEY, viaSkip ? "skipped" : "done"); } catch { /* noop */ }
    setVisible(false);
    onDone && onDone(viaSkip);
  };
  const next = () => {
    if (idx >= STEPS.length - 1) return finish(false);
    setIdx(idx + 1);
  };
  const back = () => setIdx(Math.max(0, idx - 1));

  const cardStyle = useMemo(() => {
    if (!rect) {
      // Centered fallback when anchor missing OR step is non-anchored
      return {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        maxWidth: "480px",
        width: "min(90vw, 480px)",
      };
    }
    const cardW = 400;
    const gap = 16;
    const side = step?.side || "bottom";
    let top, left;
    if (side === "top") {
      top = rect.top - 200;
      left = Math.max(16, Math.min(rect.left + rect.width / 2 - cardW / 2, window.innerWidth - cardW - 16));
    } else {
      top = rect.top + rect.height + gap;
      left = Math.max(16, Math.min(rect.left + rect.width / 2 - cardW / 2, window.innerWidth - cardW - 16));
    }
    // Keep card in the viewport vertically
    top = Math.max(16 + window.scrollY, Math.min(top, window.scrollY + window.innerHeight - 220));
    return { position: "absolute", top: `${top}px`, left: `${left}px`, width: `${cardW}px` };
  }, [rect, step]);

  if (!visible || !step) return null;

  // Sprint 60.141 — 4-panel "surround" scrim so the highlighted anchor
  // renders pixel-crisp while the rest of the page stays dimmed. Steps
  // with no anchor (welcome, conflict banner explainer) get a
  // full-screen scrim + centered coach card instead.
  const surround = rect
    ? [
        { top: 0, left: 0, width: "100vw", height: `${Math.max(0, rect.top - window.scrollY - 6)}px` },
        { top: `${rect.top - window.scrollY + rect.height + 6}px`, left: 0, width: "100vw", height: `calc(100vh - ${rect.top - window.scrollY + rect.height + 6}px)` },
        { top: `${Math.max(0, rect.top - window.scrollY - 6)}px`, left: 0, width: `${Math.max(0, rect.left - 6)}px`, height: `${rect.height + 12}px` },
        { top: `${Math.max(0, rect.top - window.scrollY - 6)}px`, left: `${rect.left + rect.width + 6}px`, width: `calc(100vw - ${rect.left + rect.width + 6}px)`, height: `${rect.height + 12}px` },
      ]
    : null;

  return (
    <div data-testid="raw-onboarding-tour" className="pointer-events-none">
      {surround ? (
        surround.map((s, i) => (
          <div
            key={i}
            onClick={() => finish(true)}
            className="pointer-events-auto fixed z-[9998] bg-black/70"
            style={s}
            data-testid={i === 0 ? "raw-tour-scrim" : undefined}
          />
        ))
      ) : (
        // No anchor found (or step is intentionally centered) —
        // full-screen dim so the coach card still reads clearly.
        <div
          onClick={() => finish(true)}
          className="pointer-events-auto fixed inset-0 z-[9998] bg-black/70"
          data-testid="raw-tour-scrim"
        />
      )}

      {/* Highlight ring */}
      {rect && (
        <div
          className="absolute z-[9999] pointer-events-none border-2 border-[#39FF14] shadow-[0_0_36px_-4px_#39FF14]"
          style={{
            top: `${rect.top - 6}px`,
            left: `${rect.left - 6}px`,
            width: `${rect.width + 12}px`,
            height: `${rect.height + 12}px`,
            borderRadius: "6px",
          }}
        />
      )}

      {/* Coach card */}
      <div
        style={cardStyle}
        className="pointer-events-auto z-[10000] bg-black border-2 border-[#39FF14] shadow-[0_0_48px_-8px_#39FF14] p-4"
        data-testid={`raw-tour-step-${step.id}`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="mono text-[9px] uppercase tracking-widest text-[#39FF14]">
            Manual Optimizer Tour · Step {idx + 1} of {STEPS.length}
          </div>
          <button
            type="button"
            onClick={() => finish(true)}
            data-testid="raw-tour-skip"
            className="text-zinc-500 hover:text-white mono text-[10px] uppercase tracking-widest"
          >
            Skip
          </button>
        </div>
        <div className="heading text-lg font-black uppercase tracking-tight text-white mb-1.5">
          {step.title}
        </div>
        <div className="text-[12px] text-zinc-300 leading-relaxed mb-4">
          {step.body}
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-1">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full ${i === idx ? "bg-[#39FF14]" : i < idx ? "bg-[#39FF14]/40" : "bg-zinc-700"}`}
              />
            ))}
          </div>
          <div className="flex gap-1.5">
            {idx > 0 && (
              <button
                type="button"
                onClick={back}
                data-testid="raw-tour-back"
                className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:border-white hover:text-white"
              >
                Back
              </button>
            )}
            <button
              type="button"
              onClick={next}
              data-testid="raw-tour-next"
              className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 bg-[#39FF14] text-black font-black hover:bg-[#2ee30d]"
            >
              {idx >= STEPS.length - 1 ? "Finish · Start Building" : "Next →"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
